"use client";

import type { Cart, LineItem } from "@spree/sdk";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { addToCart as addToCartAction, getCart as getCartAction, removeCartItem as removeCartItemAction, updateCartItem as updateCartItemAction } from "@/lib/data/cart";
interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  updating: boolean;
  itemCount: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (variantId: string, quantity?: number) => Promise<void>;
  updateItem: (lineItemId: string, quantity: number) => Promise<void>;
  removeItem: (lineItemId: string) => Promise<void>;
  refreshCart: () => Promise<void>;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children,
  initialCart = null,
  initialOpen = false
}: {
  children: ReactNode;
  /** Hydrate the cart up front (SSR / design previews) to skip the mount fetch. */
  initialCart?: Cart | null;
  /** Start with the cart drawer open (design previews). */
  initialOpen?: boolean;
}) {
  const [cart, setCart] = useState<Cart | null>(initialCart);
  const [loading, setLoading] = useState(initialCart === null);
  const [updating, setUpdating] = useState(false);
  const [isOpen, setIsOpen] = useState(initialOpen);
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("cart");
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const refreshCart = useCallback(async () => {
    try {
      const cartData = await getCartAction();
      setCart(cartData);
    } catch {
      setCart(null);
    } finally {
      setLoading(false);
    }
  }, []);
  const mutateCart = useCallback(async (action: () => Promise<{
    success: boolean;
    cart?: Cart | null;
    error?: string;
  }>, fallbackMessage: string, onSuccess?: () => void) => {
    setUpdating(true);
    try {
      const result = await action();
      if (result.success) {
        setCart(result.cart ?? null);
        onSuccess?.();
        router.refresh();
      } else {
        toast.error(result.error || fallbackMessage);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : fallbackMessage);
    } finally {
      setUpdating(false);
    }
  }, [router]);
  const addItem = useCallback(async (variantId: string, quantity = 1) => {
    await mutateCart(() => addToCartAction(variantId, quantity), t("failedToAddItem"), () => setIsOpen(true));
  }, [mutateCart, t]);
  const updateItem = useCallback(async (lineItemId: string, quantity_0: number) => {
    await mutateCart(() => updateCartItemAction(lineItemId, quantity_0), t("failedToUpdateItem"));
  }, [mutateCart, t]);
  const removeItem = useCallback(async (lineItemId_0: string) => {
    await mutateCart(() => removeCartItemAction(lineItemId_0), t("failedToRemoveItem"));
  }, [mutateCart, t]);

  // Re-fetch cart on navigation (e.g., after checkout completes, the stale
  // cart token will be cleared by getCart and the cart state will update).
  // On the order-placed page, skip refreshCart to avoid a race condition:
  // getCart() auto-clears the cart token cookie on error (completed order
  // is no longer a cart), which removes the only auth token guest users
  // have before getCheckoutOrder() can use it.
  useEffect(() => {
    // Hydrated with an initial cart (SSR / design previews) — skip the client fetch.
    if (initialCart !== null) {
      return;
    }
    if (pathname.includes("/order-placed/")) {
      setCart(null);
      setLoading(false);
      return;
    }
    refreshCart();
  }, [refreshCart, pathname, initialCart]);
  const itemCount = useMemo<number>(() => cart?.items?.reduce((sum: number, item: LineItem) => sum + item.quantity, 0) ?? 0, [cart]);
  const value = useMemo<CartContextType>(() => ({
    cart,
    loading,
    updating,
    itemCount,
    isOpen,
    openCart,
    closeCart,
    addItem,
    updateItem,
    removeItem,
    refreshCart
  }), [cart, loading, updating, itemCount, isOpen, openCart, closeCart, addItem, updateItem, removeItem, refreshCart]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}